import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { ChevronDown, Package } from 'react-feather'
import { Card, CardBody, Col, Collapse, Row } from 'reactstrap'
import CartItemsList from '../../Cart/CartItemsList'
import dollar_price from '../../general/DollarPrice'
import { buildGroupCartItems, isFreeShippingRate } from './shippingGroupsHelpers'

// Render rate details (service name, estimated days, cost)
const RateDetails = ({ rate }) => {
  const showEstimatedDays = rate?.estimated_days

  return (
    <div
      className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center w-100"
      style={{ gap: '0.25rem' }}
    >
      <p className="m-0">{rate?.service_name}</p>
      {showEstimatedDays && <span className="d-none d-sm-block">-</span>}
      {showEstimatedDays && (
        <div className="text-muted" style={{ fontSize: '0.875rem' }}>
          {rate.estimated_days} business day{rate.estimated_days !== 1 ? 's' : ''}
        </div>
      )}
      <strong className="ml-sm-auto">{dollar_price(rate?.cost_in_cents)}</strong>
    </div>
  )
}

RateDetails.propTypes = {
  rate: PropTypes.shape({
    service_name: PropTypes.string,
    estimated_days: PropTypes.number,
    cost_in_cents: PropTypes.number
  })
}

// Render shipping method header card
const ShippingMethodHeader = ({ onToggle, showChevron }) => (
  <Card onClick={onToggle} className="shipping-method-card" style={!onToggle ? { cursor: 'default' } : undefined}>
    <CardBody className="shipping-method-card-body">
      <Row>
        <Col xs={11}>
          <strong>Shipping Method</strong>
        </Col>
        {showChevron && (
          <Col xs={1} className="d-flex align-items-center justify-content-end pl-0 pr-0">
            <ChevronDown style={{ color: '#212529' }} />
          </Col>
        )}
      </Row>
    </CardBody>
  </Card>
)

ShippingMethodHeader.propTypes = {
  onToggle: PropTypes.func,
  showChevron: PropTypes.bool
}

// Render a rate option card
const RateOptionCard = ({
  rate,
  isSelected,
  onClick = () => {},
  showRadio,
  hideRadio,
  radioColSize,
  detailsColSize
}) => {
  const detailsColXs = showRadio ? radioColSize : 12
  const detailsColSm = showRadio ? detailsColSize : 12
  const radioColClassName = `px-0 align-items-center justify-content-center ${hideRadio ? 'd-none' : 'd-flex'}`

  return (
    <Card onClick={onClick} className="shipping-rate-card" style={{ cursor: showRadio ? 'cursor' : 'default' }}>
      <CardBody className={`shipping-method-card-body ${isSelected ? 'selected-shipping-rate-card' : ''}`}>
        <Row>
          {showRadio && (
            <Col xs={2} sm={1} className={radioColClassName}>
              <input
                type="radio"
                name="shipping-rate"
                value={rate?.service_code}
                checked={isSelected}
                className="shipping-rate-radio"
              />
            </Col>
          )}
          <Col xs={detailsColXs} sm={detailsColSm}>
            <RateDetails rate={rate} />
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

RateOptionCard.propTypes = {
  rate: PropTypes.shape({
    service_code: PropTypes.string,
    service_name: PropTypes.string,
    estimated_days: PropTypes.number,
    cost_in_cents: PropTypes.number
  }),
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  showRadio: PropTypes.bool,
  hideRadio: PropTypes.bool,
  radioColSize: PropTypes.number,
  detailsColSize: PropTypes.number
}

const ShippingGroup = ({ group, groupIndex, totalGroups, selectedRate, onRateSelect, cartItems }) => {
  // Use line_items from server response (includes group-specific quantity/address)
  const groupCartItems = buildGroupCartItems(group.line_items, cartItems)

  const hasRates = group.rates && group.rates.length > 0
  const isFreeShipping = !hasRates && isFreeShippingRate(selectedRate)
  const hasDestinationAddress = !!group.destination_address

  const currentSelectedRate = useMemo(
    () => group?.rates?.find(rate => rate.service_code === selectedRate?.service_code),
    [group.rates, selectedRate]
  )

  const [collapse, setCollapse] = useState(false)
  const [frozenRate, setFrozenRate] = useState(null)

  // When collapse opens, freeze the current displayed rate
  // When collapse closes, clear the frozen rate so displayedRate updates
  const onEntering = () => {
    setFrozenRate(currentSelectedRate)
  }
  const onExited = () => {
    setFrozenRate(null)
  }

  // Displayed rate is frozen when collapse is open, otherwise use currentSelectedRate
  const displayedRate = collapse && frozenRate ? frozenRate : currentSelectedRate
  const toggle = () => setCollapse(!collapse)

  return (
    <div className="shipping-group mb-4">
      <div className="shipping-group-header mb-3">
        <h5>
          <Package size={20} className="mr-2" />
          Shipping Group {groupIndex + 1} of {totalGroups}
        </h5>
      </div>
      {groupCartItems.length > 0 && (
        <div className="shipping-group-items mb-3">
          <CartItemsList
            isCheckout={true}
            filteredCartItems={groupCartItems}
            hideMultiAddressDisplay={hasDestinationAddress}
            groupDestinationAddress={group.destination_address}
          />
        </div>
      )}
      {!hasRates && !isFreeShipping && (
        <div className="alert alert-info mb-3">
          <small>⏳ Calculating shipping rates for this group...</small>
        </div>
      )}
      {isFreeShipping && (
        <div className="alert alert-success mb-3">
          <strong>Free Shipping</strong> — No shipping charges for this shipment
        </div>
      )}

      {hasRates && group.rates.length > 1 && (
        <Card>
          <CardBody className="p-0">
            <ShippingMethodHeader onToggle={toggle} showChevron={true} />
            <RateOptionCard
              rate={displayedRate}
              isSelected={selectedRate?.service_code === displayedRate?.service_code && collapse}
              onClick={() => {
                if (!collapse) toggle()
                else onRateSelect(displayedRate)
              }}
              showRadio={true}
              hideRadio={!collapse}
              radioColSize={collapse ? 9 : 12}
              detailsColSize={collapse ? 11 : 12}
            />
            <Collapse isOpen={collapse} onEntering={onEntering} onExited={onExited}>
              {group.rates
                .filter(rate => rate.service_code !== displayedRate?.service_code)
                .map(rate => (
                  <RateOptionCard
                    key={rate.service_code}
                    rate={rate}
                    isSelected={selectedRate?.service_code === rate.service_code}
                    onClick={() => onRateSelect(rate)}
                    showRadio={true}
                    radioColSize={9}
                    detailsColSize={11}
                  />
                ))}
            </Collapse>
          </CardBody>
        </Card>
      )}
      {hasRates && group.rates.length <= 1 && (
        <Card>
          <CardBody className="p-0">
            <ShippingMethodHeader showChevron={false} />
            <RateOptionCard
              rate={displayedRate}
              isSelected={false}
              // onClick={() => onRateSelect(displayedRate)}
              showRadio={false}
            />
          </CardBody>
        </Card>
      )}
    </div>
  )
}

ShippingGroup.propTypes = {
  group: PropTypes.shape({
    carrier_service_group_id: PropTypes.string.isRequired,
    line_items: PropTypes.arrayOf(
      PropTypes.shape({
        cart_item_id: PropTypes.number.isRequired,
        quantity: PropTypes.number.isRequired,
        customer_address_id: PropTypes.number
      })
    ).isRequired,
    destination_address: PropTypes.shape({
      id: PropTypes.number,
      full_name: PropTypes.string,
      address_line_1: PropTypes.string,
      address_line_2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string
    }),
    rates: PropTypes.arrayOf(
      PropTypes.shape({
        service_name: PropTypes.string.isRequired,
        service_code: PropTypes.string.isRequired,
        carrier_name: PropTypes.string.isRequired,
        cost_in_cents: PropTypes.number.isRequired,
        estimated_days: PropTypes.number.isRequired,
        encrypted_cost: PropTypes.string.isRequired,
        carrier_service_id: PropTypes.number.isRequired
      })
    ).isRequired
  }).isRequired,
  groupIndex: PropTypes.number.isRequired,
  totalGroups: PropTypes.number.isRequired,
  selectedRate: PropTypes.shape({
    service_code: PropTypes.string,
    cost_in_cents: PropTypes.number,
    encrypted_cost: PropTypes.string,
    carrier_service_id: PropTypes.number,
    cart_item_ids: PropTypes.arrayOf(PropTypes.number),
    is_free_shipping: PropTypes.bool
  }),
  onRateSelect: PropTypes.func.isRequired,
  cartItems: PropTypes.array.isRequired
}

export default ShippingGroup
